import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Chart } from 'react-google-charts';
import { getWeeklyChart } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';

const MetricsView = ({ completedExercises, exerciseData }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función para obtener el inicio y fin de la semana actual (lunes a domingo)
  const getWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado

    // Calcular cuántos días restar para llegar al lunes
    // Si es domingo (0), restar 6 días; si es lunes (1), restar 0; si es martes (2), restar 1, etc.
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Calcular el lunes de esta semana (inicio)
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysToMonday);
    monday.setHours(0, 0, 0, 0);

    // Calcular el domingo de esta semana (fin)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    // Formatear fechas como YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(monday),
      endDate: formatDate(sunday)
    };
  };

  // Cargar datos del API al montar el componente
  useEffect(() => {
    const loadWeeklyMetrics = async () => {
      try {
        const clientId = sessionStorage.getItem('clientId');
        const idUser = sessionStorage.getItem('idUser');
        const { startDate, endDate } = getWeekDates();

        if (clientId && idUser) {
          const response = await getWeeklyChart(clientId, idUser, startDate, endDate);

          if (response) {
            // Transformar los datos del API al formato del gráfico
            const formattedData = [
              [t('move.dayOfWeekLabel'), t('move.totalVideosChart'), t('move.completedVideosChart'), t('move.pendingVideosChart')],
            ];

            // Si el API devuelve un array de datos por día
            if (Array.isArray(response)) {
              response.forEach((dayData) => {
                const total = Number(dayData.totalVideos) || 0;
                const completed = Number(dayData.videosCompletados) || 0;
                const pending = Number(dayData.videosPendientes) || 0;
                const dayName = dayData.dayName || '';

                formattedData.push([
                  dayName,
                  total,
                  completed,
                  pending
                ]);
              });
            }

            setChartData(formattedData);
          }
        }
      } catch (error) {
        console.error('Error cargando métricas semanales:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWeeklyMetrics();
  }, [t]);

  // Calcular totales con validación
  const totalVideos = chartData ? chartData.slice(1).reduce((sum, row) => {
    return sum + (Number(row[1]) || 0);
  }, 0) : 0;
  const totalCompletados = chartData ? chartData.slice(1).reduce((sum, row) => {
    return sum + (Number(row[2]) || 0);
  }, 0) : 0;
  const totalPendientes = chartData ? chartData.slice(1).reduce((sum, row) => {
    return sum + (Number(row[3]) || 0);
  }, 0) : 0;

  const completionRate = totalVideos > 0
    ? ((totalCompletados / totalVideos) * 100).toFixed(1)
    : '0.0';

  if (loading) {
    return (
      <div
        className="min-vh-100 p-4 d-flex align-items-center justify-content-center"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)'
        }}
      >
        <div className="text-white text-center">
          <h3>{t('move.weeklyMetricsLoading')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100 p-4"
      style={{
        background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)'
      }}
    >
      <Container>
        {/* Gráfico de barras con Google Charts */}
        <Card className="shadow-lg mb-4" style={{ borderRadius: '24px' }}>
          <Card.Body className="p-4">
            <h5 className="mb-4">{t('move.completedVideosByDay')}</h5>
            {chartData && chartData.length > 1 ? (
              <Chart
                chartType="ColumnChart"
                width="100%"
                height="400px"
                loader={<div>{t('move.chartLoading')}</div>}
                data={chartData}
                options={{
                  legend: { position: 'top', alignment: 'center' },
                  colors: ['#94a3b8', '#10b981', '#ef4444'],
                  vAxis: {
                    title: t('move.videoQuantityLabel'),
                    minValue: 0,
                  },
                  hAxis: {
                    title: t('move.dayOfWeekLabel'),
                  },
                  chartArea: {
                    width: '85%',
                    height: '70%',
                  },
                  bar: { groupWidth: '75%' },
                }}
              />
            ) : (
              <div className="text-center p-5">
                <p className="text-muted">{t('move.noDataAvailable')}</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Métricas */}
        <Row className="g-3">
          <Col md={4}>
            <Card className="shadow h-100" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="p-3 rounded me-3"
                    style={{ backgroundColor: '#dcfce7' }}
                  >
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: '2rem',
                        color: '#16a34a'
                      }}
                    >
                      ✓
                    </div>
                  </div>
                  <div>
                    <p className="h2 fw-bold text-dark mb-0">{totalCompletados}</p>
                    <p className="text-muted mb-0">{t('move.completedVideosLabel')}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="shadow h-100" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="p-3 rounded me-3"
                    style={{ backgroundColor: '#f1f5f9' }}
                  >
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: '2rem',
                        color: '#64748b'
                      }}
                    >
                      #
                    </div>
                  </div>
                  <div>
                    <p className="h2 fw-bold text-dark mb-0">{totalVideos}</p>
                    <p className="text-muted mb-0">{t('move.totalVideosLabel')}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="shadow h-100" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="p-3 rounded me-3"
                    style={{ backgroundColor: '#fee2e2' }}
                  >
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: '2rem',
                        color: '#dc2626'
                      }}
                    >
                      ⏱
                    </div>
                  </div>
                  <div>
                    <p className="h2 fw-bold text-dark mb-0">{totalPendientes}</p>
                    <p className="text-muted mb-0">{t('move.pendingVideosLabel')}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MetricsView;